import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Skeleton from "react-loading-skeleton";
import useFetch from "../../../hook/useFetch";
import useHeaderRefresh from "../../../hook/useHeaderRefresh";
import { notificationActions } from "../../../store/notification-slice";
import "./FaqManager.css";

const emptyFaq = {
  question: "",
  answer: "",
  link: "admin.php?page=rso_crawler",
  category: "crawler",
  component_type: "product",
  component_key: "rso_wp_plugin_pro",
  sort_order: 10,
  active: 1,
};

const defaultTopics = ["sitemap", "schema", "crawler", "settings"];

export default function FaqManager() {
  const { get, post, update, del, error } = useFetch();
  const dispatch = useDispatch();
  const [faqs, setFaqs] = useState(null);
  const [searchFaqs, setSearchFaqs] = useState([]);
  const [products, setProducts] = useState([]);
  const [draft, setDraft] = useState(emptyFaq);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [component, setComponent] = useState("all");
  const [busy, setBusy] = useState(null);

  const notify = (message, status = false) => dispatch(notificationActions.setNotification({ message: String(message), status }));
  const load = () => {
    get("/admin/rso-faqs", null, notify, data => {
      const loadedFaqs = data?.data?.faqs || [];
      setFaqs(loadedFaqs);
      setSearchFaqs(loadedFaqs);
    });
    get("/admin/products", null, notify, data => setProducts(Array.isArray(data) ? data : []));
  };

  useEffect(() => { load(); }, []);
  useHeaderRefresh(load);

  const productMap = useMemo(() => Object.fromEntries(products.map(product => [product.app_code, product.title])), [products]);
  const topics = useMemo(() => [...new Set([...defaultTopics, ...(faqs || []).map(faq => faq.category).filter(Boolean)])]
    .sort((left, right) => left.localeCompare(right, "de")), [faqs]);
  const searchFaqMap = useMemo(() => new Map(searchFaqs.map(faq => [Number(faq.id), faq])), [searchFaqs]);
  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return (faqs || []).filter(faq => {
      const indexedFaq = searchFaqMap.get(Number(faq.id)) || faq;
      if (category !== "all" && indexedFaq.category !== category) return false;
      if (component !== "all" && `${indexedFaq.component_type}:${indexedFaq.component_key || ""}` !== component) return false;
      if (!term) return true;
      return [indexedFaq.question, indexedFaq.answer, indexedFaq.link, indexedFaq.category, productMap[indexedFaq.component_key], indexedFaq.component_key]
        .some(value => String(value || "").toLocaleLowerCase().includes(term));
    });
  }, [faqs, search, category, component, productMap, searchFaqMap]);

  const components = useMemo(() => {
    const values = new Set(searchFaqs.map(faq => `${faq.component_type}:${faq.component_key || ""}`));
    return [...values];
  }, [searchFaqs]);

  const payload = faq => ({
    question: faq.question,
    answer: faq.answer,
    link: faq.link || "",
    category: faq.category,
    component_type: faq.component_type,
    component_key: faq.component_type === "product" ? faq.component_key : "",
    sort_order: Number(faq.sort_order) || 0,
    active: Number(faq.active) ? 1 : 0,
  });

  const changeFaq = (id, key, value) => setFaqs(current => current.map(faq => Number(faq.id) === Number(id) ? { ...faq, [key]: value } : faq));
  const changeDraft = (key, value) => setDraft(current => ({ ...current, [key]: value }));
  const componentLabel = faq => faq.component_type === "global" ? "Global" : `Product · ${productMap[faq.component_key] || faq.component_key || "Not selected"}`;

  const createFaq = event => {
    event.preventDefault();
    if (busy) return;
    setBusy("new");
    post("/admin/rso-faq", payload(draft), message => { setBusy(null); notify(message); }, () => {
      setBusy(null);
      setDraft({ ...emptyFaq, component_key: products[0]?.app_code || emptyFaq.component_key, sort_order: (faqs?.length || 0) * 10 + 10 });
      notify("FAQ wurde hinzugefügt.", true);
      load();
    }, requestError => { setBusy(null); notify(requestError?.message || requestError); });
  };

  const saveFaq = faq => {
    if (busy) return;
    setBusy(`save-${faq.id}`);
    update("/admin/rso-faq", { id: faq.id, ...payload(faq) }, message => { setBusy(null); notify(message); }, () => {
      setBusy(null);
      notify("FAQ wurde aktualisiert.", true);
      load();
    }, requestError => { setBusy(null); notify(requestError?.message || requestError); });
  };

  const deleteFaq = faq => {
    if (busy) return;
    setBusy(`delete-${faq.id}`);
    del("/admin/rso-faq", { id: faq.id }, message => { setBusy(null); notify(message); }, () => {
      setBusy(null);
      notify("FAQ wurde gelöscht.", true);
      load();
    }, requestError => { setBusy(null); notify(requestError?.message || requestError); }).then(confirmed => {
      if (!confirmed) setBusy(null);
    });
  };

  const ProductSelect = ({ value, onChange, id }) => <select id={id} className="form-select" value={value || ""} onChange={event => onChange(event.target.value)} required>
    <option value="">Select product …</option>
    {products.map(product => <option key={product.id} value={product.app_code}>{product.title} · {product.app_code}</option>)}
  </select>;

  return <section className="faq-admin rso-pages">
    <header className="faq-admin__hero">
      <div><span className="faq-admin__eyebrow">RSO CONTENT</span><h1>FAQ-Verwaltung</h1><p>FAQs zentral in der API pflegen und global oder einem konkreten Produkt zuordnen.</p></div>
      <div className="faq-admin__stats"><strong>{faqs?.length ?? "–"}</strong><span>FAQs gesamt</span><small>{faqs ? `${faqs.filter(faq => Number(faq.active)).length} aktiv` : "Lädt …"}</small></div>
    </header>

    {error && <div className="alert alert-danger">{String(error)}</div>}

    <form className="faq-admin__create" onSubmit={createFaq}>
      <div className="faq-admin__section-title"><div><span>NEUER EINTRAG</span><h2>FAQ hinzufügen</h2></div><button className="btn btn-primary" disabled={!!busy}>{busy === "new" ? "Wird hinzugefügt …" : "FAQ hinzufügen"}</button></div>
      <div className="faq-admin__form-grid">
        <label className="faq-admin__wide"><span>Frage</span><input className="form-control" value={draft.question} onChange={event => changeDraft("question", event.target.value)} maxLength="500" required /></label>
        <label className="faq-admin__wide"><span>Antwort</span><textarea className="form-control" rows="4" value={draft.answer} onChange={event => changeDraft("answer", event.target.value)} maxLength="10000" required /></label>
        <label><span>Thema</span><input className="form-control" list="faq-topic-options" value={draft.category} onChange={event => changeDraft("category", event.target.value)} maxLength="30" placeholder="z. B. Onboarding" required /><datalist id="faq-topic-options">{topics.map(item => <option key={item} value={item} />)}</datalist></label>
        <label><span>Component</span><select className="form-select" value={draft.component_type} onChange={event => changeDraft("component_type", event.target.value)}><option value="global">Global</option><option value="product">Product</option></select></label>
        {draft.component_type === "product" && <label><span>Product</span><ProductSelect id="faq-new-product" value={draft.component_key} onChange={value => changeDraft("component_key", value)} /></label>}
        <label><span>Reihenfolge</span><input className="form-control" type="number" min="0" max="100000" value={draft.sort_order} onChange={event => changeDraft("sort_order", event.target.value)} /></label>
        <label className="faq-admin__wide"><span>Admin-Link (optional)</span><input className="form-control" value={draft.link} onChange={event => changeDraft("link", event.target.value)} placeholder="admin.php?page=rso_crawler" /></label>
        <label className="faq-admin__check"><input type="checkbox" checked={!!Number(draft.active)} onChange={event => changeDraft("active", event.target.checked ? 1 : 0)} /><span>Sofort aktiv</span></label>
      </div>
    </form>

    <div className="faq-admin__toolbar">
      <label><span>Suche</span><input className="form-control" type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Frage, Antwort oder Produkt …" /></label>
      <label><span>Thema</span><select className="form-select" value={category} onChange={event => setCategory(event.target.value)}><option value="all">Alle Themen</option>{topics.map(item => <option key={item}>{item}</option>)}</select></label>
      <label><span>Component</span><select className="form-select" value={component} onChange={event => setComponent(event.target.value)}><option value="all">All components</option>{components.map(value => { const [type, key] = value.split(":"); return <option key={value} value={value}>{type === "global" ? "Global" : `Product · ${productMap[key] || key}`}</option>; })}</select></label>
      <div><span>Ergebnis</span><strong>{filtered.length} von {faqs?.length || 0}</strong></div>
    </div>

    {faqs === null ? <Skeleton count={12} /> : <div className="faq-admin__list">
      {filtered.map(faq => <article key={faq.id} className={`faq-admin__item${Number(faq.active) ? "" : " faq-admin__item--inactive"}`}>
        <div className="faq-admin__item-head"><div><span className={`faq-admin__status ${Number(faq.active) ? "is-active" : ""}`}>{Number(faq.active) ? "Aktiv" : "Inaktiv"}</span><span className="faq-admin__category">{faq.category}</span><span className="faq-admin__component">{componentLabel(faq)}</span></div><strong>#{faq.id}</strong></div>
        <div className="faq-admin__form-grid">
          <label className="faq-admin__wide"><span>Frage</span><input className="form-control" value={faq.question || ""} onChange={event => changeFaq(faq.id, "question", event.target.value)} maxLength="500" /></label>
          <label className="faq-admin__wide"><span>Antwort</span><textarea className="form-control" rows="4" value={faq.answer || ""} onChange={event => changeFaq(faq.id, "answer", event.target.value)} maxLength="10000" /></label>
          <label><span>Thema</span><input className="form-control" list="faq-topic-options" value={faq.category || ""} onChange={event => changeFaq(faq.id, "category", event.target.value)} maxLength="30" required /></label>
          <label><span>Component</span><select className="form-select" value={faq.component_type} onChange={event => changeFaq(faq.id, "component_type", event.target.value)}><option value="global">Global</option><option value="product">Product</option></select></label>
          {faq.component_type === "product" && <label><span>Product</span><ProductSelect id={`faq-product-${faq.id}`} value={faq.component_key} onChange={value => changeFaq(faq.id, "component_key", value)} /></label>}
          <label><span>Reihenfolge</span><input className="form-control" type="number" min="0" max="100000" value={faq.sort_order || 0} onChange={event => changeFaq(faq.id, "sort_order", event.target.value)} /></label>
          <label className="faq-admin__wide"><span>Admin-Link</span><input className="form-control" value={faq.link || ""} onChange={event => changeFaq(faq.id, "link", event.target.value)} /></label>
          <label className="faq-admin__check"><input type="checkbox" checked={!!Number(faq.active)} onChange={event => changeFaq(faq.id, "active", event.target.checked ? 1 : 0)} /><span>FAQ aktiv</span></label>
        </div>
        <div className="faq-admin__actions"><button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => saveFaq(faq)}>{busy === `save-${faq.id}` ? "Speichert …" : "Änderungen speichern"}</button><button type="button" className="btn btn-outline-danger" disabled={!!busy} onClick={() => deleteFaq(faq)}>{busy === `delete-${faq.id}` ? "Löscht …" : "Löschen"}</button></div>
      </article>)}
      {!filtered.length && <div className="faq-admin__empty"><strong>Keine FAQs gefunden</strong><span>Passe Suche oder Filter an oder lege einen neuen Eintrag an.</span></div>}
    </div>}
  </section>;
}
